'use client'

import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import {
  ROOM_ZOOM_LEVEL,
  TMarkerResponse,
  TMarkerSearchParams,
  useMapStore,
  useSearchFilterStore,
} from '@/entities/map'
import {
  eventManager,
  mapManager,
  NaverMap,
  useUserLocationButton,
} from '@/shared/lib'
import { TLanguage } from '@/shared/model'

import { useBoundCircle } from '../lib/bound-circle'
import { useMapEvent } from '../lib/map-event'
import { getMapOptions } from '../lib/map-option'
import { useOverlays } from '../lib/overlays'

const MAP_ID = 'room-search-map'

type Props = {
  language?: TLanguage
  getMarkers: (
    params: TMarkerSearchParams,
  ) => Promise<TMarkerResponse | undefined>
  onInitialized?: () => void
}

export const SearchMap = ({ language, getMarkers, onInitialized }: Props) => {
  const t = useTranslations('map')

  const [scriptLoaded, setScriptLoaded] = useState(false)

  const searchParams = useSearchParams()

  useBoundCircle()

  const { createUserLocationButton } = useUserLocationButton({
    minZoomLevel: ROOM_ZOOM_LEVEL,
  })

  const overlays = useOverlays()

  const updateOverlays = async (options: { resetRoom?: boolean } = {}) => {
    const { params } = useMapStore.getState()
    const { filters } = useSearchFilterStore.getState()

    const markers = await getMarkers({ ...params, ...filters })

    if (markers) {
      overlays.update(markers, options)
    }
  }

  const { addMapEventListeners, handleMapChange } = useMapEvent({
    updateOverlays,
    clearOverlays: overlays.clear,
  })

  const initializeMap = () => {
    const lat = searchParams?.get('lat') ?? null
    const lng = searchParams?.get('lng') ?? null
    const zoomLevel = searchParams?.get('zoomLevel') ?? null

    const options = getMapOptions({ lat, lng, zoomLevel })
    mapManager.initialize({ ...options, id: MAP_ID })
  }

  const initialize = () => {
    if (typeof window === 'undefined') return

    initializeMap()

    handleMapChange({ initial: true })
    addMapEventListeners()

    eventManager.addEventListenerOnce('init', () => {
      // Location 버튼 위치를 상단에 위치시키기 위해 Location 버튼 생성 후 줌 컨트롤 옵션 설정
      createUserLocationButton()
      mapManager.setZoomControl()
    })

    onInitialized?.()
  }

  const handleScriptLoaded = () => {
    setScriptLoaded(true)
  }

  const handleMapAuthFailure = () => {
    alert(t('error'))
  }

  useEffect(() => {
    if (!scriptLoaded) return

    initialize()
  }, [scriptLoaded])

  useEffect(() => {
    window.navermap_authFailure = handleMapAuthFailure

    const filterUnsubscribe = useSearchFilterStore.subscribe(
      state => state.filters,
      () => {
        eventManager.triggerEvent('update')
      },
    )

    return () => {
      filterUnsubscribe()
      overlays.clear()
      eventManager.clearAllListeners()

      window.navermap_authFailure = null
      mapManager.reset()
    }
  }, [])

  return (
    <NaverMap id={MAP_ID} language={language} onReady={handleScriptLoaded} />
  )
}
